import React, { useEffect, useMemo, useState } from 'react';
import {
  UserPlus, Plus, Mail, Shield, Users, Package, Workflow, Database, HelpCircle, CheckSquare, GitBranch, Zap
} from 'lucide-react';
import { processBuilderStorage } from '../../services/processBuilderStorage';
import { flowAutomationStorage } from '../../services/flowAutomationStorage';
import * as processBuilderStorageRouter from '../../services/processBuilderStorageRouter';
import * as flowAutomationStorageRouter from '../../services/flowAutomationStorageRouter';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import {
  useQuickActions,
  useSetupTasks,
  useSetupStats,
  useRecentlyViewed,
  useToggleSetupTask,
  formatRelativeTime,
  calculateTaskCompletion
} from '../../services/queries/setupQueries';
import { useSetupChanges } from '../../hooks/useSetupChanges';
import { useObjects } from '../../services/queries/objectQueries';
import { useUsers } from '../../services/queries/userQueries';
import { DevModeBanner } from '../common/DevModeBanner';
import { useRecentItems } from '../../hooks/useRecentItems';
import { StandardButton, StandardLoadingState } from '../standard';

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  UserPlus,
  Plus,
  Mail,
  Shield,
  Users,
  Package,
  Workflow,
  Database,
  CheckSquare,
  GitBranch
};

// ============================================
// INTERFACES
// ============================================

interface SetupHomeProps {
  onNavigateToSection?: (section: string) => void;
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface QuickActionCardProps {
  icon: string;
  title: string;
  description: string;
  onClick: () => void;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({ icon, title, description, onClick }) => {
  const IconComponent = iconMap[icon] || HelpCircle;

  return (
    <button
      onClick={onClick}
      className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all group text-left"
    >
      <div className="mr-3 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-800/50 transition-colors">
        <IconComponent className="h-5 w-5 text-purple-600 dark:text-purple-400" />
      </div>
      <div>
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
      </div>
    </button>
  );
};

interface SetupChangeItemProps {
  action: string;
  target?: string; // Optional - only present for nested entities like fields
  user: string;
  time: string;
}

const SetupChangeItem: React.FC<SetupChangeItemProps> = ({ action, target, user, time }) => {
  return (
    <div className="flex items-start py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex-1">
        <p className="text-sm text-gray-900 dark:text-gray-100">
          <span className="font-medium">{user}</span> {action}
          {target && <>{' '}<span className="font-medium text-purple-600 dark:text-purple-400">{target}</span></>}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{time}</p>
      </div>
    </div>
  );
};

interface SetupTaskProps {
  completed: boolean;
  title: string;
  onToggle: () => void;
}

const SetupTask: React.FC<SetupTaskProps> = ({ completed, title, onToggle }) => {
  return (
    <label className="flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors">
      <input
        type="checkbox"
        checked={completed}
        onChange={onToggle}
        className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 focus:ring-2 cursor-pointer"
      />
      <span className={`ml-3 text-sm ${completed ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
        {title}
      </span>
    </label>
  );
};

interface StatItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

const StatItem: React.FC<StatItemProps> = ({ icon: Icon, label, value }) => {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center">
        <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-3" />
        <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</span>
    </div>
  );
};

interface RecentItemProps {
  label: string;
  time: string;
}

const RecentItem: React.FC<RecentItemProps> = ({ label, time }) => {
  return (
    <div className="py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <p className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-1">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{time}</p>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Setup Home
 *
 * Main dashboard for system administrators to customize and configure the Product Hub system.
 * Displays quick actions, recent setup changes, recommended setup tasks with progress tracking,
 * setup overview statistics (users, objects, fields, workflows, storage), and recently viewed items.
 * Provides centralized access to all system configuration features.
 *
 * Data Source: setupQueries.ts (React Query with mock data)
 *
 * @component
 */
export const SetupHome: React.FC<SetupHomeProps> = ({ onNavigateToSection }) => {
  const [showAllChanges, setShowAllChanges] = React.useState(false);

  // Fetch all data with React Query
  const { data: quickActions, isLoading: loadingActions } = useQuickActions();
  const { data: setupTasks, isLoading: loadingTasks } = useSetupTasks();
  const { data: stats, isLoading: loadingStats } = useSetupStats();
  const { data: recentlyViewed, isLoading: loadingRecent } = useRecentlyViewed();

  // Fetch real data for dynamic stats
  const { data: objects } = useObjects();
  const { data: users } = useUsers({ status: 'active' });

  const toggleTask = useToggleSetupTask();
  const { addRecentItem } = useRecentItems();

  // Use dynamic setup changes instead of mock data
  const { getRecentChanges, getRelativeTime: getChangeRelativeTime, changes: allChanges } = useSetupChanges();
  const recentChanges = getRecentChanges(4);
  const displayedChanges = showAllChanges ? allChanges : recentChanges;

  // Track page view in Recent Items
  useEffect(() => {
    addRecentItem({
      id: 'setup-home',
      title: 'Setup Home',
      subtitle: 'Administration & Configuration',
      path: '/admin?section=setup',
      type: 'page',
      icon: 'Settings'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [processAutomationStats, setProcessAutomationStats] = useState<{
    activeProcesses: number;
    activeFlows: number;
  } | null>(null);

  useEffect(() => {
    if (!USE_GOBODO_API) return;
    let cancelled = false;
    Promise.all([
      processBuilderStorageRouter.getStatsAsync(),
      flowAutomationStorageRouter.getStatsAsync()
    ])
      .then(([pbStats, flowStats]) => {
        if (cancelled) return;
        setProcessAutomationStats({
          activeProcesses: pbStats?.activeProcesses ?? 0,
          activeFlows: flowStats?.activeFlows ?? 0
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[SetupHome] Gobodo process/flow stats failed, using zeros:', err?.message ?? err);
        setProcessAutomationStats({ activeProcesses: 0, activeFlows: 0 });
      });
    return () => { cancelled = true; };
  }, []);

  const dynamicStats = React.useMemo(() => {
    if (!objects || !users) return null;

    const customObjectsCount = objects.filter(obj => obj.isCustom && obj.isActive).length;
    const customFieldsCount = objects
      .filter(obj => obj.isCustom && obj.isActive)
      .reduce((total, obj) => total + obj.fieldCount, 0);
    const activeUsersCount = users.length;

    const activeProcessesCount = USE_GOBODO_API
      ? (processAutomationStats?.activeProcesses ?? 0)
      : processBuilderStorage.getStats().activeProcesses;
    const activeFlowsCount = USE_GOBODO_API
      ? (processAutomationStats?.activeFlows ?? 0)
      : flowAutomationStorage.getStats().activeFlows;

    return {
      activeUsers: activeUsersCount,
      customObjects: customObjectsCount,
      customFields: customFieldsCount,
      activeWorkflows: stats?.activeWorkflows || 5,
      activeProcesses: activeProcessesCount,
      activeFlows: activeFlowsCount,
      storageUsedGB: stats?.storageUsedGB || 12.4,
      storageTotalGB: stats?.storageTotalGB || 100
    };
  }, [objects, users, stats, processAutomationStats]);

  // Calculate task completion
  const taskProgress = setupTasks ? calculateTaskCompletion(setupTasks) : null;

  // Map action paths to section IDs for navigation
  const handleQuickAction = (actionPath: string) => {
    if (!onNavigateToSection) {
      console.log('Navigate to:', actionPath);
      return;
    }

    // Map action paths to admin section IDs
    const sectionMap: Record<string, string> = {
      '/admin/users/new': 'users',
      '/admin/object-manager': 'object-manager',
      '/admin/email-templates/new': 'email-templates',
      '/admin/roles': 'roles'
    };

    const section = sectionMap[actionPath];
    if (section) {
      onNavigateToSection(section);
    } else {
      console.log('Unknown action path:', actionPath);
    }
  };

  return (
    <div className="setup-home p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Setup Home</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize and configure your Product Hub system
        </p>
      </div>

      {/* Development Mode Banner */}
      <DevModeBanner
        dataSource="mockDataManager.ts"
        itemCount="setup statistics and recent activities"
        customMessage="Development Mode: Setup data persisted via mockDataManager.ts (localStorage). Changes survive page refresh."
        dismissible={true}
      />

      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Main Content */}
        <div className="col-span-12 lg:col-span-8 space-y-6">

          {/* Quick Actions Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h2>
            {loadingActions ? (
              <StandardLoadingState message="Loading actions..." size="sm" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {quickActions?.map((action) => (
                  <QuickActionCard
                    key={action.id}
                    icon={action.icon}
                    title={action.title}
                    description={action.description}
                    onClick={() => handleQuickAction(action.action)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Recent Setup Changes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Recent Setup Changes</h2>
              {allChanges.length > 4 && (
                <StandardButton
                  variant="link"
                  size="sm"
                  onClick={() => setShowAllChanges(!showAllChanges)}
                >
                  {showAllChanges ? 'Show Less' : `View All (${allChanges.length})`}
                </StandardButton>
              )}
            </div>
            {displayedChanges.length === 0 ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
                No recent setup changes. Changes will appear here when you create, update, or delete system configuration items.
              </div>
            ) : (
              <div className="space-y-3">
                {displayedChanges.map((change) => (
                  <SetupChangeItem
                    key={change.id}
                    action={change.description}
                    target={change.targetName}
                    user={change.performedBy}
                    time={getChangeRelativeTime(change.timestamp)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Recommended Setup Tasks */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Recommended Setup Tasks
            </h2>
            {loadingTasks ? (
              <StandardLoadingState message="Loading tasks..." size="sm" />
            ) : (
              <>
                {taskProgress && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600 dark:text-gray-400">
                        {taskProgress.completed} of {taskProgress.total} completed
                      </span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">
                        {taskProgress.percentage}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${taskProgress.percentage}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {setupTasks?.map((task) => (
                    <SetupTask
                      key={task.id}
                      completed={task.completed}
                      title={task.title}
                      onToggle={() => toggleTask.mutate({
                        taskId: task.id,
                        completed: !task.completed
                      })}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="col-span-12 lg:col-span-4 space-y-6">

          {/* Setup Overview Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Setup Overview</h2>
            {!dynamicStats ? (
              <StandardLoadingState message="Loading stats..." size="sm" />
            ) : (
              <div className="space-y-4">
                <StatItem icon={Users} label="Active Users" value={dynamicStats.activeUsers.toString()} />
                <StatItem icon={Package} label="Custom Objects" value={dynamicStats.customObjects.toString()} />
                <StatItem icon={Plus} label="Custom Fields" value={dynamicStats.customFields.toString()} />
                <StatItem icon={Zap} label="Active Workflows" value={dynamicStats.activeWorkflows.toString()} />
                <StatItem icon={GitBranch} label="Active Processes" value={dynamicStats.activeProcesses.toString()} />
                <StatItem icon={Workflow} label="Active Flows" value={dynamicStats.activeFlows.toString()} />
                <StatItem icon={Database} label="Storage Used" value={`${dynamicStats.storageUsedGB} GB of ${dynamicStats.storageTotalGB} GB`} />
              </div>
            )}
          </div>

          {/* Setup Resources */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Resources</h2>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-center">
              <HelpCircle className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Documentation and training resources coming soon
              </p>
            </div>
          </div>

          {/* Recently Viewed */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Recently Viewed</h2>
            {loadingRecent ? (
              <StandardLoadingState message="Loading..." size="sm" />
            ) : (
              <div className="space-y-2 text-sm">
                {recentlyViewed?.map((item) => (
                  <RecentItem
                    key={item.id}
                    label={item.path}
                    time={formatRelativeTime(item.timestamp)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupHome;
